// src/utils/reviewMessaging.js
//
// Pass 75 — the ONE canonical domain for the reviewed-video → notification/message loop. Every review-
// delivery side effect and every conversation write goes through here so they are DETERMINISTIC, IDEMPOTENT
// and MANUAL-STATE-SAFE:
//   · deliverReviewedVideo — ONLY after the canonical video is genuinely `reviewed`, creates ONE deterministic
//     review thread + ONE deterministic intro message + ONE create-once feedback_ready notification. A retry
//     recovers only MISSING artifacts; it never reorders an active conversation nor resets read state.
//   · sendThreadMessage — ONE canonical writer: one message doc (stable id → retry-safe), one thread
//     latest-message update, one recipient unread bit, one create-once new_message notification.
//   · markThreadRead — clears ONLY the caller's own unread bit.
//
// VIDEO reviewer identity is an Auth UID (buildThreadId(videoId, reviewerAuthUid, studentAuthUid)) — the
// video assignment model, NOT the lesson two-ID model. clientId != studentId is never bridged here.

import { doc, getDoc, setDoc, updateDoc, runTransaction, serverTimestamp, arrayRemove } from 'firebase/firestore';
import { buildThreadId } from './threadNavigation';

export const REVIEW_THREAD_TYPE = 'video_review';
export const MAX_MESSAGE_LEN = 4000;

// Deterministic logical-event ids (source-neutral) so create-once side effects converge under retry/concurrency.
export const reviewIntroMessageId = (videoId) => `review_ready__${videoId}`;
export const reviewFeedbackNotificationId = (videoId) => `feedback_ready__${videoId}`;
export const newMessageNotificationId = (messageId) => `new_message__${messageId}`;

// Pass 85.1B.2 (§9–§17) — UPGRADE-SAFE lazy delivery recovery. The pre-85.1B.1 DEPLOYED flow wrote the source (thread /
// message) and THEN the deterministic notification in a SEPARATE step, so a failure could leave "source EXISTS,
// notification MISSING". The 85.1B.1 atomic flow returns early once the source exists, so that OLD partial state is no
// longer repaired by a retry — violating the durable Pass-75 recovery invariant. This ensures the deterministic
// notification WITHOUT restoring actor read of the recipient's private notification and WITHOUT inferring "missing" from a
// permission-denied:
//   • getDoc(notification): the notifications read rule allows `resource == null`, so an ABSENT notification returns a
//     snapshot with exists()==false — MISSING is proven by a SUCCESSFUL null read, NEVER by a denial (§10). A PRESENT
//     notification the actor may not read throws permission-denied which — because `resource == null` is allowed — can
//     ONLY mean the doc EXISTS; we treat it as already-delivered and write NOTHING (read/readAt/createdAt preserved — §13/§16).
//   • On a confirmed-absent notification we setDoc the deterministic payload; the notification CREATE rule governs it, and
//     a doc that raced into existence turns the write into an UPDATE the whitelist denies (→ throws, never a silent reset).
//   • A genuine (non-permission) read failure or a create denial THROWS and stays observable (§17).
export async function ensureNotificationDelivered(db, notificationId, payload = {}) {
  if (!notificationId) return { existed: false, recovered: false };
  const ref = doc(db, 'notifications', notificationId);
  let snap;
  try {
    snap = await getDoc(ref);
  } catch (err) {
    const s = String((err && (err.code || err.message)) || '');
    if (/permission[-_ ]?denied/i.test(s) || /PERMISSION_DENIED/.test(s)) {
      return { existed: true, recovered: false }; // present + unreadable ⟺ already delivered — preserve, touch nothing
    }
    throw err; // a genuine read failure stays observable
  }
  if (snap.exists()) return { existed: true, recovered: false };
  await setDoc(ref, { read: false, createdAt: serverTimestamp(), deliveryChannels: ['in_app'], ...payload });
  return { existed: false, recovered: true };
}

// The other participant in a 2-party thread (participantIds = [reviewerAuthUid, studentAuthUid]).
export function otherParticipant(participantIds = [], uid = '') {
  const parts = Array.isArray(participantIds) ? participantIds.filter(Boolean) : [];
  return parts.find((p) => p !== uid) || '';
}

// Derived unread state (§21): a thread is unread FOR a participant when their uid is in thread.unreadBy.
export function threadUnreadFor(thread = {}, uid = '') {
  const list = Array.isArray(thread.unreadBy) ? thread.unreadBy : [];
  return !!uid && list.includes(uid);
}
export function unreadThreadCount(threads = [], uid = '') {
  return (threads || []).filter((t) => threadUnreadFor(t, uid)).length;
}

// §7/§8/§36 — ENTITY-IDEMPOTENT review thread. Creates the canonical thread + deterministic intro message ONLY
// when the thread does not already exist. If the thread exists (a prior delivery, possibly with later
// conversation) it performs ZERO writes — never reordering lastMessage/unread. A crash between thread-create and
// intro-create is recovered because the intro has a deterministic id and is create-once.
//
// Pass 85.1B.1 (§13) — CREATE-ONCE WITHOUT ACTOR READ BYPASS. When a `feedbackNotification` is supplied it is written
// in the SAME transaction as the thread, so "thread exists ⟺ feedback_ready notification exists" is an atomic
// invariant. The idempotency existence check is the ACTOR-READABLE thread (the reviewer is a thread participant) —
// the reviewer NEVER reads the recipient student's private notification. Total-failure recovery = retry re-runs the
// whole atomic transaction; there is no partial "thread but no notification" state to require actor-read recovery.
export async function ensureReviewThread(db, {
  videoId = '', reviewerAuthUid = '', studentAuthUid = '', participantId = '', title = 'Video Feedback', instructorName = 'Instructor',
  organizationId = '', relatedBookingId = '', relatedLessonId = '', relatedServiceId = '', feedbackNotification = null,
} = {}) {
  if (!videoId || !reviewerAuthUid || !studentAuthUid) return { threadId: null, created: false };
  const threadId = buildThreadId(videoId, reviewerAuthUid, studentAuthUid);
  const threadRef = doc(db, 'threads', threadId);
  const introMessage = `Your video "${title}" has been reviewed by ${instructorName}.`;

  let created = false;
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(threadRef);
    if (snap.exists()) { created = false; return; } // §8 already delivered — ZERO writes, no reorder (notification too)
    tx.set(threadRef, {
      participantIds: [reviewerAuthUid, studentAuthUid],
      threadType: REVIEW_THREAD_TYPE,
      studentId: studentAuthUid,
      clientUserId: studentAuthUid,
      instructorId: reviewerAuthUid,          // VIDEO reviewer Auth uid (NOT the business instructorId)
      videoId,
      organizationId,
      // Pass 85.1B — the CANONICAL student tenancy: the video's participantId (blank for a legacy video → the thread is
      // staff-only; set for a modern video → the owning student reads via participantSelfByProfile). Stamped ONLY when
      // present so a legacy thread keeps no participantId (create rule requires thread.participantId == video.participantId).
      ...(String(participantId || '') ? { participantId: String(participantId) } : {}),
      relatedBookingId, relatedLessonId, relatedServiceId,
      title,
      instructorName,
      lastMessage: introMessage,
      lastMessageTimestamp: serverTimestamp(),
      lastMessageSenderId: reviewerAuthUid,
      unreadBy: [studentAuthUid],             // student sees the new conversation as unread; the reviewer does not
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // (organizationId retained for lineage/consumers; the create rule pins the tenant via the video's participants)
    });
    // §13 — the feedback_ready notification, written ATOMICALLY with the thread on first delivery. The notification
    // create rule binds it to the reviewed video (get(video) sees committed state); the reviewer is the actor. It is a
    // blind create because thread-absent ⟺ notification-absent, so there is nothing to overwrite (read state never reset).
    if (feedbackNotification && feedbackNotification.notificationId) {
      const { notificationId, ...notifData } = feedbackNotification;
      tx.set(doc(db, 'notifications', notificationId), {
        createdAt: serverTimestamp(), deliveryChannels: ['in_app'], ...notifData,
      });
    }
    created = true;
  });

  // Ensure the deterministic intro message exists (create-once). The thread is now committed, so the message
  // create rule's parent-thread participant check passes. Never rewrite an existing intro (no reorder).
  const introRef = doc(db, 'threads', threadId, 'messages', reviewIntroMessageId(videoId));
  const introSnap = await getDoc(introRef);
  if (!introSnap.exists()) {
    await setDoc(introRef, {
      message: introMessage, from: 'instructor', userId: reviewerAuthUid,
      timestamp: serverTimestamp(), read: false,
    });
  }
  return { threadId, created };
}

// §3/§4/§5 — deliver the reviewed-video side effects. VERIFIES the canonical persisted state first (the video is
// genuinely `reviewed` and the caller is its reviewer) — never trusting stale component state — then ensures the
// deterministic thread + intro + create-once feedback_ready notification. Idempotent recovery on retry.
export async function deliverReviewedVideo(db, { videoId = '', reviewerAuthUid = '' } = {}) {
  if (!videoId || !reviewerAuthUid) throw new Error('videoId and reviewerAuthUid are required.');
  const videoSnap = await getDoc(doc(db, 'videos', videoId));
  if (!videoSnap.exists()) throw new Error('Video not found.');
  const v = videoSnap.data() || {};
  if (v.status !== 'reviewed') throw new Error('Review delivery requires a reviewed video.');
  if ((v.reviewedBy || '') !== reviewerAuthUid) throw new Error('Only the completing reviewer may deliver this review.');

  const studentAuthUid = v.userId || '';
  if (!studentAuthUid) throw new Error('Reviewed video has no student owner.');
  const title = v.videoName || v.title || 'Video Feedback';
  const participantId = String(v.participantId || ''); // Pass 85.1B — canonical student tenancy carried from the video

  // Pass 85.1B.1 (§13) — the feedback_ready notification is delivered ATOMICALLY with the review thread (create-once
  // without an actor-read of the recipient's private notification). A MODERN notification carries the video's canonical
  // participantId so the owning student reads it via participantSelfByProfile; a legacy video (no participantId) leaves
  // it blank (staff-only). The payload is built ONCE and reused for the atomic write and the upgrade recovery below.
  const feedbackNotifId = reviewFeedbackNotificationId(videoId);
  const feedbackPayload = {
    userId: studentAuthUid,
    ...(participantId ? { participantId, audience: 'participant' } : {}), // Pass 85.1B.3 — explicit participant audience (legacy no-participant → no audience → fail-closed)
    organizationId: v.organizationId || '',
    type: 'feedback_ready',
    title: v.reviewAudioURL ? 'Feedback and audio are ready' : 'Feedback is ready',
    message: v.reviewAudioURL
      ? `Your video "${title}" has timestamped feedback and an audio note ready.`
      : `Your video "${title}" has been reviewed and is ready to watch.`,
    link: `/reviews/${videoId}`,
    actorUserId: reviewerAuthUid,
    actorName: v.reviewedByName || 'Instructor',
    relatedEntityType: 'video',
    relatedEntityId: videoId,
    read: false,
  };

  const { threadId, created } = await ensureReviewThread(db, {
    videoId, reviewerAuthUid, studentAuthUid, participantId, title,
    instructorName: v.reviewedByName || 'Instructor',
    organizationId: v.organizationId || '',
    relatedBookingId: v.bookingId || '', relatedLessonId: v.lessonId || '', relatedServiceId: v.serviceId || '',
    feedbackNotification: { notificationId: feedbackNotifId, ...feedbackPayload },
  });

  // Pass 85.1B.2 (§9/§14) — UPGRADE RECOVERY. On the atomic first delivery the notification was written inside the thread
  // transaction (created === true) so nothing more is needed. When the thread ALREADY existed (a pre-85.1B.1 delivery may
  // have left the thread but no feedback_ready notification), lazily ensure the deterministic notification: it is created
  // only if genuinely absent, and an already-delivered notification is preserved untouched (no read/createdAt reset).
  if (!created) {
    await ensureNotificationDelivered(db, feedbackNotifId, feedbackPayload);
  }

  return { threadId };
}

// §17/§18/§19 — the ONE canonical message-send writer. A STABLE messageId (per UI send attempt) makes retry
// idempotent: the message + thread latest-message + recipient unread bit are ONE atomic transaction; the
// new_message notification is create-once. Reusing the same id with identical content = idempotent success;
// reusing it with conflicting sender/content = fail closed.
export async function sendThreadMessage(db, {
  threadId = '', senderUid = '', senderName = '', text = '', messageId = '',
} = {}) {
  const body = String(text || '').trim();
  if (!threadId || !senderUid || !messageId) throw new Error('threadId, senderUid and messageId are required.');
  if (!body) throw new Error('Message text is required.');
  if (body.length > MAX_MESSAGE_LEN) throw new Error('Message is too long.');

  const threadRef = doc(db, 'threads', threadId);
  const msgRef = doc(db, 'threads', threadId, 'messages', messageId);

  const threadSnap = await getDoc(threadRef);
  if (!threadSnap.exists()) throw new Error('Thread not found.');
  const thread = threadSnap.data() || {};
  const recipient = otherParticipant(thread.participantIds, senderUid);
  // Pass 85.1B — when the recipient is the STUDENT (the thread's studentId), the new_message notification carries the
  // thread's canonical participantId + org so the student reads it via participantSelfByProfile; when the recipient is
  // the staff reviewer, it carries only the org (read via the staff branch), never a foreign participant.
  const toStudent = !!recipient && recipient === String(thread.studentId || '');
  const threadParticipantId = String(thread.participantId || '');
  const threadOrganizationId = String(thread.organizationId || '');

  const newMsgNotifId = newMessageNotificationId(messageId);
  // The new_message notification payload, built ONCE — used for the atomic write and the upgrade recovery below.
  // Recipient is the STUDENT → carry the thread's canonical participant+org (read via participantSelfByProfile); recipient
  // is the staff reviewer → carry only the org (read via the staff/ops recipient branch), never a foreign participant.
  // Pass 85.1B.3 — explicit AUDIENCE: a message to the STUDENT with the thread's participant → 'participant'; to the STAFF
  // reviewer → 'organization_staff' (read via the org-staff branch); a student recipient on a LEGACY thread (no
  // participantId) carries NO audience → fail-closed. Audience/participantId stay internally consistent (create rule §13).
  const newMsgAudience = (toStudent && threadParticipantId) ? 'participant' : (!toStudent ? 'organization_staff' : '');
  const newMsgPayload = recipient ? {
    userId: recipient,
    type: 'new_message',
    title: senderName ? `New message from ${senderName}` : 'New message',
    message: body.slice(0, 120) + (body.length > 120 ? '…' : ''),
    link: `/inbox?tab=messages&thread=${threadId}`,
    actorUserId: senderUid, actorName: senderName || '',
    ...(threadOrganizationId ? { organizationId: threadOrganizationId } : {}),
    ...(toStudent && threadParticipantId ? { participantId: threadParticipantId } : {}),
    ...(newMsgAudience ? { audience: newMsgAudience } : {}),
    read: false,
    relatedEntityType: 'thread', relatedEntityId: threadId,
    metadata: { messageId },
  } : null;

  // Pass 85.1B.1 (§13) — the message, the thread latest/unread update AND the recipient's new_message notification are
  // ONE atomic transaction. Idempotency is proven by the ACTOR-READABLE message doc (own thread), so the sender NEVER
  // reads the recipient's private notification — a student can no longer direct-read a STAFF recipient's notification,
  // and there is no actor-read bypass. Message-absent ⟺ notification-absent, so a same-id replay returns early with the
  // notification already delivered (blind create, read state never reset); a conflicting replay fails closed.
  let wasReplay = false;
  await runTransaction(db, async (tx) => {
    const existing = await tx.get(msgRef);
    if (existing.exists()) {
      const m = existing.data() || {};
      if (m.userId === senderUid && String(m.message || '') === body) { wasReplay = true; return; } // idempotent same-id replay
      throw new Error('MESSAGE_ID_CONFLICT');
    }
    tx.set(msgRef, { message: body, from: 'user', userId: senderUid, timestamp: serverTimestamp(), read: false });
    tx.update(threadRef, {
      lastMessage: body,
      lastMessageTimestamp: serverTimestamp(),
      lastMessageSenderId: senderUid,
      unreadBy: recipient ? [recipient] : [],   // recipient's conversation becomes unread; sender's does not
      updatedAt: serverTimestamp(),
    });
    if (newMsgPayload) {
      tx.set(doc(db, 'notifications', newMsgNotifId), {
        createdAt: serverTimestamp(), deliveryChannels: ['in_app'], ...newMsgPayload,
      });
    }
  });

  // Pass 85.1B.2 (§9/§15) — UPGRADE RECOVERY. On a fresh send the notification was written atomically with the message.
  // When the message ALREADY existed (a pre-85.1B.1 send may have left the message but no new_message notification), a
  // same-id replay lazily ensures the deterministic notification — created only if genuinely absent, an already-delivered
  // one preserved untouched. The message is never duplicated, the thread latest/unread is never rewritten (the tx returned).
  if (wasReplay && newMsgPayload) {
    await ensureNotificationDelivered(db, newMsgNotifId, newMsgPayload);
  }

  return { messageId, threadId };
}

// §21/§23 — clear ONLY the caller's own unread bit (idempotent). Never clears the other participant's.
export async function markThreadRead(db, threadId = '', uid = '') {
  if (!threadId || !uid) return;
  try { await updateDoc(doc(db, 'threads', threadId), { unreadBy: arrayRemove(uid) }); } catch { /* best-effort */ }
}
